import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Sparkles } from '../components/Icons.jsx';

const SECTIONS = [
  { id: 'vr', label: 'VR — Verbal Reasoning', min: 300, max: 900 },
  { id: 'dm', label: 'DM — Decision Making', min: 300, max: 900 },
  { id: 'qr', label: 'QR — Quantitative Reasoning', min: 300, max: 900 },
  { id: 'sjt', label: 'SJT', type: 'band', bands: ['A', 'B', 'C', 'D'] },
];

const SJT_BANDS = {
  A: { label: 'Band A', desc: 'Top performers — responses closely match expert consensus. Very rare.', colour: '#166534', bg: '#dcfce7' },
  B: { label: 'Band B', desc: 'Good performance — in the majority for a competitive candidate.', colour: '#1e40af', bg: '#dbeafe' },
  C: { label: 'Band C', desc: 'Weak SJT — often costs offers at threshold schools.', colour: '#b45309', bg: '#fef3c7' },
  D: { label: 'Band D', desc: 'Poor performance — significant ethical-reasoning gap.', colour: '#991b1b', bg: '#fee2e2' },
};

function scoreColour(score) {
  if (score >= 800) return '#166534';
  if (score >= 700) return '#1e40af';
  if (score >= 600) return '#b45309';
  return '#991b1b';
}

function sectionGrade(score) {
  if (score >= 800) return { label: 'Strong', colour: '#166534' };
  if (score >= 700) return { label: 'Good', colour: '#1e40af' };
  if (score >= 600) return { label: 'Borderline', colour: '#b45309' };
  return { label: 'Weak', colour: '#991b1b' };
}

const COACHING = {
  vr: {
    Strong: 'VR is a genuine strength. In sessions focus on speed maintenance under pressure — the gap between practice and test conditions is where VR scores drop. If scoring >800, drill time management (22-25 questions, ~1.5 min each).',
    Good: 'VR is solid but has headroom. Target: are errors from misreading questions or from weak inference skills? Drill "can\'t tell" accuracy — it\'s the most mis-answered option. One targeted passage per session, timed.',
    Borderline: 'VR needs focused work. Session agenda: 1) Identify the error type (speed vs inference vs vocabulary). 2) Chunk-reading drill for 10 min. 3) Two timed passages with a "true/false/can\'t tell" breakdown. Don\'t do un-timed passages — timing pressure is the skill.',
    Weak: 'VR is the priority. Root causes to investigate: a) Reading speed below 250 WPM, b) Over-relying on background knowledge, c) Missing negation words ("not", "only", "except"). Prescribe the Reading Lab chunk drill and memory trainer as homework. Plan 2 VR-focused sessions before moving to other sections.',
  },
  dm: {
    Strong: 'DM strength — maintain it. Advanced work: 3-circle Venn in under 90 seconds, Bayesian updating in probability trees, Strongest Argument recognition. Quiz them on the trickier syllogism forms (affirming the consequent).',
    Good: 'DM is good. Pinpoint the question type costing marks: Venn, Syllogisms, Probability, Strongest Argument, or Reading Maths? One type will dominate. Run a targeted 10-question set on that type only.',
    Borderline: 'DM needs structure. Run the Decision Profiler drill to identify which sub-type is weakest. Most borderline DM candidates are losing marks on either probability (calculation errors) or syllogisms (logical form confusion). Address these separately.',
    Weak: 'DM is urgent. Start with Venn Architect (the most mark-dense question type). Then Probability Reasoning drill. Syllogisms can wait — Venn and probability together can add 50+ points. One DM session per week minimum.',
  },
  qr: {
    Strong: 'QR is under control. Push toward 99th percentile: complex data tables, multi-step ratio problems, and UCAT-format speed/distance/time under 40 seconds. The Conversion Gauntlet sprint mode will stress-test this.',
    Good: 'QR is good but not dominant. Focus on numpad accuracy (practise using the calculator under pressure) and any consistent error type — percentages, proportion problems, or unit conversion. 5 minutes of mental arithmetic warm-up per session.',
    Borderline: 'QR is leaking marks. Two common causes: 1) Slow calculator technique — time 10 basic multiplications, they should take under 3 seconds each. 2) Missing the two-step in word problems (calculate intermediate result, THEN apply to question). Assign the Mental Arithmetic and Numpad Trainer drills as daily 5-minute homework.',
    Weak: 'QR is the biggest score opportunity — maths skills are trainable faster than verbal reasoning. Diagnostic: can they do 15% of 340 in under 8 seconds without a calculator? If not, mental arithmetic fluency comes first. Then proceed to UCAT-format data questions.',
  },
  sjt: {
    A: 'Band A SJT — exceptional. Spend minimal session time on SJT. Advanced extension only: ethics of AI in medicine, resource allocation edge cases, research ethics. These won\'t change their band but build interview depth.',
    B: 'Band B SJT — solid foundation. To push toward A: focus on the near-miss scenarios (Very Appropriate vs Appropriate — the partial-credit zone). Run the Near-Miss Trainer drill. Identify if they lose marks on patient safety escalation or on confidentiality scenarios.',
    C: 'Band C SJT — this is costing offers at Band B threshold schools (e.g., several London med schools). Root cause: do they understand the escalation hierarchy? Do they know the GMC\'s 4 domains? Run GMC Standards drill. The SJT Discussion Cards tutor module works well here — project a scenario and have them defend their rating.',
    D: 'Band D SJT — requires an ethics reset. Start with GMC Good Medical Practice framework — make them explain each domain in their own words. Move to concrete scenarios: patient safety, confidentiality, consent. The Social Media & AI in Healthcare drill covers newer content. Expect 2-3 SJT-focused sessions before retesting.',
  },
};

export default function TutorMockReview() {
  const [scores, setScores] = useState({ vr: '', dm: '', qr: '', sjt: '' });
  const [generated, setGenerated] = useState(false);

  const totalNumeric = ['vr', 'dm', 'qr'].reduce((sum, k) => sum + (Number(scores[k]) || 0), 0);
  const totalMax = 2700;
  const allFilled = scores.vr && scores.dm && scores.qr && scores.sjt;

  const generate = () => {
    if (allFilled) setGenerated(true);
  };

  const reset = () => {
    setScores({ vr: '', dm: '', qr: '', sjt: '' });
    setGenerated(false);
  };

  return (
    <>
      <Link to="/tutor-portal" className="back-link"><ArrowLeft width="16" height="16" />Back to tutor portal</Link>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Tutor Portal · Mock Review Walkthrough</div>
        <h1>Turn a mock score into a coaching plan.</h1>
        <p className="subtitle">Enter the student's section breakdown. Get a prioritised coaching agenda with talking points, drill prescriptions, and session targets for each weak area.</p>
      </div>

      {!generated ? (
        <div className="card" style={{ padding: '24px 26px', maxWidth: 560 }}>
          <h3 style={{ margin: '0 0 18px', fontSize: 18 }}>Enter mock breakdown</h3>
          {SECTIONS.map((s) => (
            <div key={s.id} style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>{s.label}</label>
              {s.type === 'band' ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  {s.bands.map((b) => (
                    <button key={b} type="button" onClick={() => setScores({ ...scores, [s.id]: b })} style={{
                      padding: '10px 18px', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer',
                      border: `2px solid ${scores[s.id] === b ? SJT_BANDS[b].colour : 'var(--border)'}`,
                      background: scores[s.id] === b ? SJT_BANDS[b].bg : '#fff',
                      color: scores[s.id] === b ? SJT_BANDS[b].colour : 'var(--text-2)',
                    }}>{b}</button>
                  ))}
                </div>
              ) : (
                <input
                  type="number"
                  min={s.min} max={s.max}
                  value={scores[s.id]}
                  onChange={(e) => setScores({ ...scores, [s.id]: e.target.value })}
                  placeholder={`${s.min}–${s.max}`}
                  style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border-strong)', fontSize: 16, fontWeight: 600, width: 120, color: 'var(--text)' }}
                />
              )}
            </div>
          ))}
          <button onClick={generate} disabled={!allFilled} className="btn btn-primary" style={{ marginTop: 8, opacity: allFilled ? 1 : 0.5 }}>
            <Sparkles width="16" height="16" /> Generate coaching plan <ArrowRight width="14" height="14" />
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 22 }}>
            {['vr', 'dm', 'qr'].map((k) => {
              const val = Number(scores[k]);
              const grade = sectionGrade(val);
              return (
                <div key={k} className="card" style={{ padding: '14px 16px', borderLeft: `4px solid ${grade.colour}`, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{k.toUpperCase()}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: scoreColour(val), marginTop: 2 }}>{val}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: grade.colour }}>{grade.label}</div>
                </div>
              );
            })}
            <div className="card" style={{ padding: '14px 16px', borderLeft: `4px solid ${SJT_BANDS[scores.sjt]?.colour}`, textAlign: 'center', background: SJT_BANDS[scores.sjt]?.bg }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>SJT</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: SJT_BANDS[scores.sjt]?.colour, marginTop: 2 }}>Band {scores.sjt}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: SJT_BANDS[scores.sjt]?.colour }}>{SJT_BANDS[scores.sjt]?.desc.split('—')[0].trim()}</div>
            </div>
            <div className="card" style={{ padding: '14px 16px', gridColumn: 'span 2', textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total (VR+DM+QR)</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: scoreColour(totalNumeric / 3), marginTop: 2 }}>{totalNumeric} / {totalMax}</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>
            {['vr', 'dm', 'qr'].map((k) => {
              const val = Number(scores[k]);
              const grade = sectionGrade(val);
              const advice = COACHING[k][grade.label];
              return (
                <div key={k} className="card" style={{ padding: '20px 24px', borderLeft: `4px solid ${grade.colour}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{k.toUpperCase()} — {val}</div>
                    <span style={{ padding: '3px 10px', borderRadius: 999, background: grade.colour + '22', color: grade.colour, fontSize: 12, fontWeight: 700 }}>{grade.label}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: 'var(--text-2)' }}>{advice}</p>
                </div>
              );
            })}
            <div className="card" style={{ padding: '20px 24px', borderLeft: `4px solid ${SJT_BANDS[scores.sjt]?.colour}`, background: SJT_BANDS[scores.sjt]?.bg + '55' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>SJT — Band {scores.sjt}</div>
                <span style={{ padding: '3px 10px', borderRadius: 999, background: SJT_BANDS[scores.sjt]?.bg, color: SJT_BANDS[scores.sjt]?.colour, fontSize: 12, fontWeight: 700 }}>{SJT_BANDS[scores.sjt]?.desc.split('—')[0].trim()}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: 'var(--text-2)' }}>{COACHING.sjt[scores.sjt]}</p>
            </div>
          </div>

          <button onClick={reset} className="btn btn-secondary">
            ← Enter new scores
          </button>
        </>
      )}
    </>
  );
}
